<template>
    <ac-modal :title="'Email Template: ' + emailTypeDesc" ref="emailTemplateModal" :size-class="'modal-unlayer'">
        <div class="container-fluid email-template-layout">
            <div class="row email-template-layout-row">
                <div class="col-lg-12 form-group">
                    <div class="info-board info-board-blue">
                        <div v-if="instructions" v-html="instructions"></div>
                        <div v-else>
                            This email is triggered immediately to any member upon enrollment in this plan.
                            You may include any special variables, along with images, or hyperlinks.
                            Special variables can be used in the subject and body of the email:
                            {{emailVariables}}
                            <br /><br />
                            Example: {{example}}"
                            <br /><br />
                            Note: Any logos that are added must reference a publicly available image URL.
                        </div>
                    </div>
                </div>
                <div class="col-lg-12 form-group">
                    <label>Subject:</label>
                    <input v-if="!previewMode" type="text" class="form-control" v-model='emailSubjectLine'>
                    <div v-else>{{previewSubject}}<hr style="margin-bottom: 0;" /></div>
                </div>
                <div class="col-lg-12 form-group email-editor-slot">
                    <label>Body:</label>
                    <ac-unlayer-email-editor
                        v-if="editorMounted && !previewMode"
                        ref="unlayerEditor"
                        class="email-editor-fill"
                        v-model="emailBody"
                        :design-json="emailDesignJson">
                    </ac-unlayer-email-editor>
                    <iframe
                        v-if="previewMode"
                        class="email-preview-frame email-editor-fill"
                        title="Email preview"
                        :srcdoc="previewBody">
                    </iframe>
                </div>
            </div>
            <ac-auto-reset-modal ref="EmailOverrideModal" :path="'pages/root/components/shared/EmailOverrideModal.vue'">
            </ac-auto-reset-modal>
        </div>
        <template slot="buttons">
            <ac-button v-if="!previewMode" :action="previewOn" class="btn btn-blue btn-sm pull-left">
                VERIFY AND SEND TEST EMAIL
            </ac-button>
            <ac-button v-else :action="previewOff" class="btn btn-blue btn-sm pull-left">
                RETURN TO EDITOR VIEW
            </ac-button>
            <ac-button :action="save" class="btn btn-green btn-sm">SAVE</ac-button>
            <ac-button :action="closeModal" class="btn btn-red btn-sm">CANCEL</ac-button>
        </template>
    </ac-modal>
</template>

<script>
    import {common} from "../../../../global/common.js";
    import {api} from "../../../../global/framework/api.js";
    import {notifications} from "../../../../global/notifications.js";

    export default {
        name: "EditEmailTemplates",
        inject: ['saveTemplate'],
        data() {
            return {
                email: null,
                emailTypeId: null,
                emailTypeDesc: "",
                emailSubjectLine: "",
                emailBody: "",
                emailDesignJson: "",
                instructions: "",
                previewSubject: "",
                previewBody: "",
                previewMode: false,
                editorMounted: false,
            }
        },
        computed: {
            emailVariables() {
                return "{{FullName}}, {{WhiteLabelUrl}}, {{WhiteLabelName}}, {{WhiteLabelEmail}}, {{RegistrationLink}}, {{PlanStartDate}}"
            },
            example() {
                return '"Hello {{FullName}}, welcome!" will be sent as "Hello John Doe, welcome!';
            }
        },
        methods: {
            closeModal() {
                this.editorMounted = false;
                this.previewMode = false;
                this.$refs.emailTemplateModal.close();
            },
            showModal(email) {
                this.previewOff();
                this.editorMounted = false;
                this.email = email;
                this.emailTypeId = email.emailTypeId;
                this.emailTypeDesc = email.emailTypeDesc;
                this.emailSubjectLine = email.emailSubjectLine || email.subjectDefault;
                this.emailBody = email.emailBody || email.bodyDefault;
                this.emailDesignJson = email.emailDesignJson || "";
                this.instructions = email.instructions;
                this.$refs.emailTemplateModal.open();
                this.$nextTick(() => {
                    this.editorMounted = true;
                });
            },
            async save() {
                if (!(await this.syncEditorContent())) return;
                if (!this.validateEmail()) return;
                this.email.emailSubjectLine = this.emailSubjectLine;
                this.email.emailBody = this.emailBody;
                this.email.emailDesignJson = this.emailDesignJson;
                this.saveTemplate(this.email);
            },
            async syncEditorContent() {
                if (!this.$refs.unlayerEditor) {
                    return true;
                }
                try {
                    const result = await this.$refs.unlayerEditor.exportContent();
                    this.emailBody = result.html;
                    this.emailDesignJson = result.designJson;
                    return true;
                } catch (e) {
                    notifications.show({title: "Email editor is still loading. Please try again."}, common.ACRESSA_NOTIFICATIONS.ERROR);
                    return false;
                }
            },
            validateEmail() {
                if(this.emailSubjectLine == null || this.emailSubjectLine == "") {
                    notifications.show({title: "Subject is required."}, common.ACRESSA_NOTIFICATIONS.ERROR);
                    return false;
                }

                if(this.emailBody == null || this.emailBody.trim() === '' ||
                    (this.$refs.unlayerEditor && this.$refs.unlayerEditor.isEmpty(this.emailBody))) {
                    notifications.show({title: "Body is required."}, common.ACRESSA_NOTIFICATIONS.ERROR);
                    return false;
                }
                return true;
            },
            async previewOn() {
                if (!(await this.syncEditorContent())) return;
                if (!this.validateEmail()) return;

                let adminEmail = await api.getNoId("Shared", "GetCurrentUserEmailAddress");
                let email = await this.$refs.EmailOverrideModal.showModal(adminEmail);
                if(email != ""){
                    var previewEmail = { emailSubjectLine: this.emailSubjectLine, emailBody: this.emailBody, emailAddress: email };
                    let result = await api.post('utilities', 'templateVerification', previewEmail, true);

                    if (result) {
                        this.previewSubject = result.emailSubjectLine;
                        this.previewBody = result.emailBody;
                        this.previewMode = true;
                    }
                }
            },
            previewOff() {
                this.previewMode = false;
            },
        },
    }
</script>

<style scoped>
    .pull-left {
        float: left;
    }

    .email-template-layout,
    .email-template-layout-row {
        display: flex;
        flex-direction: column;
        min-height: 100%;
    }

    .email-template-layout-row {
        margin-left: 0;
        margin-right: 0;
        flex: 1 1 auto;
    }

    .email-template-layout:before,
    .email-template-layout:after,
    .email-template-layout-row:before,
    .email-template-layout-row:after {
        display: none !important;
        content: none !important;
    }

    .email-template-layout-row > [class*="col-"] {
        float: none;
        width: 100%;
        flex: 0 0 auto;
    }

    .email-template-layout .info-board {
        max-height: 110px;
        overflow-y: auto;
        margin-bottom: 0;
    }

    .email-editor-slot {
        flex: 1 1 auto !important;
        min-height: 500px;
        display: flex;
        flex-direction: column;
        margin-bottom: 12px;
    }

    .email-editor-slot label {
        flex: 0 0 auto;
    }

    .email-editor-fill {
        flex: 1 1 auto;
        min-height: 500px;
        width: 100%;
    }

    .email-preview-frame {
        width: 100%;
        height: 100%;
        min-height: 400px;
        border: 1px solid #ddd;
        background: #fff;
    }
</style>

<style>
    .modal-unlayer {
        width: 96%;
        max-width: 1400px;
        height: calc(100vh - 20px);
        margin: 10px auto;
        display: flex;
        flex-direction: column;
    }

    .modal-unlayer .modal-content {
        height: 100%;
        display: flex;
        flex-direction: column;
    }

    .modal-unlayer .modal-header {
        flex: 0 0 auto;
    }

    .modal-unlayer .modal-body {
        flex: 1 1 auto;
        min-height: 0;
        max-height: none;
        overflow-x: hidden;
        overflow-y: auto;
        display: block;
        padding-bottom: 16px;
    }

    .modal-unlayer .modal-body > div {
        min-height: 100%;
        height: auto;
        display: block;
    }

    .modal-unlayer .modal-footer {
        flex: 0 0 auto;
        margin-top: 0;
    }
</style>
